import { AuthEnums, WintSalaryEnums } from "models/enums";
import { SalaryDeviationFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { CountryHelper, DateHelper, EnumHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import {
  WintSuperkollAPIModelWintSalaryDeviations as WintSalaryDeviationsApi,
  WintSuperkollAPIModelWintSalaryReport,
} from "wint-api-client/dist/types";
import useAPIClientHelper from "./useAPIClientHelper";
import SalaryDeviationHelper from "components/wintSalary/forms/salaryDeviationHelper";
import {
  AgreementDrainDeviationTypeId,
  BenefitNetDeductionGrossUpDeviationTypeId,
  SavedSalaryDepositCorrectionDeviationTypeId,
  TargetGrossSalaryDeviationTypeId,
  TargetGrossSalaryPercentageTaxDeviationTypeId,
} from "utils/constants/salaryConstants";

/**
 * Helper to normalize date for comparison (compare by date string YYYY-MM-DD in local timezone).
 * Uses local timezone methods to avoid UTC conversion issues where dates near midnight
 * could shift to a different day.
 */
const getDateKey = (date: Date | string): string => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Caps hours and minutes so that total minutes (hours * 60 + minutes) does not exceed maxTotalMinutes.
 * @param hours - Hours component
 * @param minutes - Minutes component
 * @param maxTotalMinutes - Maximum allowed total minutes
 * @returns Capped { hours, minutes }
 */
const capHoursAndMinutesToMax = (
  hours: number,
  minutes: number,
  maxTotalMinutes: number
): { hours: number; minutes: number } => {
  const safeHours = Math.max(0, isNaN(hours) ? 0 : hours);
  const safeMinutes = Math.max(0, isNaN(minutes) ? 0 : minutes);
  const totalMinutes = safeHours * 60 + safeMinutes;
  const cappedTotal = Math.min(totalMinutes, Math.max(0, maxTotalMinutes));
  return {
    hours: Math.floor(cappedTotal / 60),
    minutes: cappedTotal % 60,
  };
};

function useWintSalaryDeviationService() {
  const { apiClientInstance } = useAPIClientHelper();

  const excludedDeviationTypeIds: (string | undefined)[] = [
    TargetGrossSalaryDeviationTypeId,
    TargetGrossSalaryPercentageTaxDeviationTypeId,
    SavedSalaryDepositCorrectionDeviationTypeId,
    BenefitNetDeductionGrossUpDeviationTypeId,
    AgreementDrainDeviationTypeId,
  ];

  const createDeviationApi = (req: WintSalaryDeviationsApi.SalaryDeviationCreateRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryDeviationClient.CreateDeviation(req);
  };

  const createDeviationReApproveApi = (req: WintSalaryDeviationsApi.SalaryDeviationCreateRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryDeviationClient.CreateDeviationMutable(req);
  };

  const updateDeviationApi = (id: string, req: WintSalaryDeviationsApi.SalaryDeviationUpdateRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryDeviationClient.UpdateDeviation(id, req);
  };

  const updateDeviationReApproveApi = (id: string, req: WintSalaryDeviationsApi.SalaryDeviationUpdateRequest) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryDeviationClient.UpdateDeviationMutable(id, req);
  };

  const getDeviationByIdApi = (id: string, yearMonthFrom?: number, yearMonthTo?: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryDeviationClient.GetDeviation(id, yearMonthFrom, yearMonthTo);
  };

  const getDeviationTypesApi = async (filter: WintSalaryDeviationsApi.SalaryDeviationTypeFilter) => {
    const response = await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryDeviationClient.GetDeviationTypes(filter);
    return response.data ?? [];
  };

  const deleteDeviationApi = (id: string, yearMonthFrom?: number, yearMonthTo?: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryDeviationClient.DeleteDeviation(id, yearMonthFrom, yearMonthTo);
  };

  const deleteDeviationReApproveApi = (id: string, yearMonthFrom?: number, yearMonthTo?: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryDeviationClient.DeleteDeviationMutable(id, yearMonthFrom, yearMonthTo);
  };

  const getDeviationReportApi = (filter: WintSalaryDeviationsApi.DeviationReportFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryDeviationClient.GetSalaryDeviationReport(filter);
  };

  const getDailyHoursTemplateApi = (filter: WintSalaryDeviationsApi.DailyHoursTemplateFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).WintSalaryDeviationClient.GetDailyHoursTemplate(filter);
  };

  const getChildrenForPersonApi = async (personId: number) => {
    const client = apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    });
    const response = await client.WintSalaryDeviationClient.GetChildrenForPerson(personId);
    return response.data;
  };

  /**
   * Pot balances for one employee. showErrors is off deliberately: the withdrawal section renders
   * the failure inline, and a toast on top of that is noise. Core answers
   * WithdrawalBalancesNotAvailable until the Wint.Salary client package carries the read.
   */
  const getWithdrawalBalancesApi = async (personId: number) => {
    const client = apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: false,
    });
    const response = await client.WintSalaryDeviationClient.GetWithdrawalBalances(personId);
    return response.data;
  };

  const createChildForPersonApi = async (personId: number, request: WintSalaryDeviationsApi.ChildForCreate) => {
    const client = apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    });
    const response = await client.WintSalaryDeviationClient.CreateChildForPerson(personId, request);
    return response.data;
  };

  /**
   * Fetches daily hours template from API
   * @param filter - Filter containing personId, startDate, and endDate
   * @returns Array of DayMinutesTemplate with default hours and max limits per day
   */
  const getDailyHoursTemplate = async (
    filter: WintSalaryDeviationsApi.DailyHoursTemplateFilter
  ): Promise<WintSalaryDeviationsApi.DayMinutesTemplate[]> => {
    const response = await getDailyHoursTemplateApi(filter);
    return response.data ?? [];
  };

  const maxMinutesPerDay = 1440;

  /**
   * Generates default dayHours for WorkingHours category (skips API call).
   * Creates a date range with default values (or defaultTime when provided), capped to maxTotalMinutes: 1440 (24 hours).
   * Preserves existing user inputs for matching dates.
   *
   * @param startDate - Start date of the range
   * @param endDate - End date of the range
   * @param existingDayHours - Optional existing form data to merge with
   * @param defaultTime - Optional default time (hours/minutes); when omitted uses 8 hours, 0 minutes. Always capped to max.
   * @returns Array of HoursByDayWithMeta for the form
   */
  const generateDefaultDayHours = (
    startDate: Date,
    endDate: Date,
    existingDayHours?: SalaryDeviationFormTypes.HoursByDayWithMeta[],
    defaultTime?: { hours: number; minutes: number }
  ): SalaryDeviationFormTypes.HoursByDayWithMeta[] => {
    const existingByDate = new Map<string, SalaryDeviationFormTypes.HoursByDayWithMeta>();
    if (existingDayHours) {
      existingDayHours.forEach((dayHour) => {
        if (dayHour.day) {
          existingByDate.set(getDateKey(dayHour.day), dayHour);
        }
      });
    }

    const result: SalaryDeviationFormTypes.HoursByDayWithMeta[] = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      const dateKey = getDateKey(current);
      const existing = existingByDate.get(dateKey);

      if (existing) {
        const capped = capHoursAndMinutesToMax(existing.hours, existing.minutes, maxMinutesPerDay);
        result.push({
          ...existing,
          hours: capped.hours,
          minutes: capped.minutes,
          maxTotalMinutes: maxMinutesPerDay,
        });
      } else {
        const hours = defaultTime?.hours ?? 8;
        const minutes = defaultTime?.minutes ?? 0;
        const capped = capHoursAndMinutesToMax(hours, minutes, maxMinutesPerDay);
        result.push({
          id: UtilsHelper.getUniqueId(),
          day: new Date(current),
          hours: capped.hours,
          minutes: capped.minutes,
          maxTotalMinutes: maxMinutesPerDay,
        });
      }
      current.setDate(current.getDate() + 1);
    }

    return result;
  };

  /**
   * Converts API DayMinutesTemplate response to form HoursByDayWithMeta type.
   * Merges with existing user inputs - preserving hours/minutes for matching dates
   * while updating maxTotalMinutes from the new template.
   *
   * @param templates - Array of DayMinutesTemplate from API
   * @param existingDayHours - Optional existing form data to merge with
   * @param defaultTime - Optional default time to use if no existing time is found
   * @returns Array of HoursByDayWithMeta for the form
   */
  const convertDailyHoursTemplateToForm = (
    templates: WintSalaryDeviationsApi.DayMinutesTemplate[],
    existingDayHours?: SalaryDeviationFormTypes.HoursByDayWithMeta[],
    defaultTime?: { hours: number; minutes: number }
  ): SalaryDeviationFormTypes.HoursByDayWithMeta[] => {
    const existingByDate = new Map<string, SalaryDeviationFormTypes.HoursByDayWithMeta>();
    if (existingDayHours) {
      existingDayHours.forEach((dayHour) => {
        if (dayHour.day) {
          existingByDate.set(getDateKey(dayHour.day), dayHour);
        }
      });
    }

    return templates.map((template, index) => {
      const templateDateKey = getDateKey(template.day);
      const existing = existingByDate.get(templateDateKey);

      if (existing) {
        const capped = capHoursAndMinutesToMax(existing.hours, existing.minutes, template.totalMaxMinutes);
        return {
          ...existing,
          hours: capped.hours,
          minutes: capped.minutes,
          maxTotalMinutes: template.totalMaxMinutes,
        };
      }

      const hours = defaultTime?.hours ?? template.defaultHours;
      const minutes = defaultTime?.minutes ?? template.defaultMinutes;
      const capped = capHoursAndMinutesToMax(hours, minutes, template.totalMaxMinutes);

      return {
        id: UtilsHelper.getUniqueId(),
        day: template.day instanceof Date ? template.day : new Date(template.day),
        hours: capped.hours,
        minutes: capped.minutes,
        maxTotalMinutes: template.totalMaxMinutes,
      };
    });
  };

  const convertCreateFormToApiPost = (
    form: SalaryDeviationFormTypes.WintDeviationCreate
  ): WintSalaryDeviationsApi.SalaryDeviationCreateRequest => {
    const request = buildCreateRequest(form);
    // The form already clears the opt-in when the chosen type cannot take it; re-checking the type
    // here means no future setValues path can submit a flag the salary service would reject, and
    // keeps the rule in one place rather than in each builder.
    return {
      ...request,
      useCurrentMonthPlacement:
        request.useCurrentMonthPlacement && SalaryDeviationHelper.allowsCurrentMonthPlacementChoice(form.deviation),
    };
  };

  const buildCreateRequest = (
    form: SalaryDeviationFormTypes.WintDeviationCreate
  ): WintSalaryDeviationsApi.SalaryDeviationCreateRequest => {
    switch (form.deviationType) {
      case WintSalaryEnums.SalaryDeviationType.Absence:
        return createTimeDeviationRequest(form);
      case WintSalaryEnums.SalaryDeviationType.WorkingHours:
        if (SalaryDeviationHelper.isDecimalValueDeviation(form.deviation)) {
          return createAmountRequest(form);
        }
        return createTimeDeviationRequest(form);
      case WintSalaryEnums.SalaryDeviationType.Compensation:
        if (SalaryDeviationHelper.isFuelBenefit(form.deviation)) {
          return createFuelBenefitRequest(form);
        }
        if (SalaryDeviationHelper.shouldShowWithdrawalSection(form.deviationType, form.deviation)) {
          return createWithdrawalRequest(form);
        }
        if (SalaryDeviationHelper.isTravelCompensation(form.deviation)) {
          return createTravelCompensationRequest(form);
        }
        return createAmountRequest(form);
      case WintSalaryEnums.SalaryDeviationType.TimeBalances:
        return createTimeBalancesRequest(form);
      default:
        throw new Error(`Unsupported deviation type: ${form.deviationType}`);
    }
  };

  const convertUpdateFormToApiPut = (
    form: SalaryDeviationFormTypes.WintDeviationCreate,
    yearMonthFrom?: number,
    yearMonthTo?: number
  ): WintSalaryDeviationsApi.SalaryDeviationUpdateRequest => {
    const createRequest = convertCreateFormToApiPost(form);
    // Comment is only valid for Compensation deviations (backend:
    // CommentNotAllowedForNonCompensationDeviation). Guard the update path so a
    // comment prefilled from an existing deviation is never resubmitted for a
    // non-Compensation type (the comment field is hidden for those).
    const isCompensation = form.deviationType === WintSalaryEnums.SalaryDeviationType.Compensation;
    return {
      fromDate: createRequest.fromDate,
      toDate: createRequest.toDate,
      comment: isCompensation ? createRequest.comment : undefined,
      value: createRequest.value,
      dayMinutes: createRequest.dayMinutes,
      amountDescriptions: createRequest.amountDescriptions,
      childId: createRequest.childId,
      travelCompensation: (createRequest as WintSalaryDeviationsApi.SalaryDeviationBase).travelCompensation,
      fuelBenefit: createRequest.fuelBenefit,
      yearMonth: yearMonthFrom,
      yearMonthTo: yearMonthTo,
    };
  };

  const createTimeDeviationRequest = (
    form: SalaryDeviationFormTypes.WintDeviationCreate
  ): WintSalaryDeviationsApi.SalaryDeviationCreateRequest => {
    const { from, to } = getEffectiveDateRange(
      form.timeDeviation?.dateRange?.start,
      form.timeDeviation?.dateRange?.end
    );
    const dayHours = generateDateHours(form.timeDeviation!, form.deviationType);
    return {
      personId: Number(form.employee?.id),
      deviationTypeId: form.deviation?.id != null ? String(form.deviation.id) : "",
      fromDate: from,
      toDate: to,
      comment: form.comment,
      dayMinutes: dayHours,
      childId: form.child?.id != null ? String(form.child.id) : undefined,
      useCurrentMonthPlacement: form.useCurrentMonthPlacement ?? false,
    };
  };

  /**
   * Creates API request for amount-based deviations (DecimalValue informationType)
   * Used by WorkingHours and Compensation types with DecimalValue
   */
  const createAmountRequest = (
    form: SalaryDeviationFormTypes.WintDeviationCreate
  ): WintSalaryDeviationsApi.SalaryDeviationCreateRequest => {
    const fromDate = DateHelper.getFirstDayOfMonth(form.amount?.yearMonth ?? new Date());
    const toDate = DateHelper.getLastDayOfMonth(form.amount?.yearMonth ?? new Date());

    const isHours = SalaryDeviationHelper.isHoursDeviation(form.deviation);
    // Commission carries exactly one entry (the backend rejects more). Its description is the editable
    // deal label, mirrored into the comment because the payslip row is labelled from the comment —
    // the same text the engine writes into both.
    const commissionLabel = SalaryDeviationHelper.isCommission(form.deviation)
      ? form.amount?.sumDescriptions[0]?.description
      : undefined;

    return {
      personId: Number(form.employee?.id),
      deviationTypeId: form.deviation?.id != null ? String(form.deviation.id) : "",
      fromDate: fromDate,
      toDate: toDate,
      comment: commissionLabel ?? form.comment,
      dayMinutes: [],
      childId: form.child?.id != null ? String(form.child.id) : undefined,
      useCurrentMonthPlacement: form.useCurrentMonthPlacement ?? false,
      amountDescriptions:
        form.amount?.sumDescriptions.map(
          (sumDescription): WintSalaryDeviationsApi.AmountDescription => ({
            amount: isHours
              ? +(sumDescription.hours ?? 0) + +(sumDescription.minutes ?? 0) / 60
              : (sumDescription.sum ?? 0),
            description: sumDescription.description,
          })
        ) ?? [],
    };
  };

  /**
   * Creates API request for comp-time (Komp / TimeBalances) deviations.
   *
   * A monthly deviation captured as a single signed number of hours: the value is
   * written to the scalar `value` field (never dayMinutes / amountDescriptions).
   * The month is expanded to a first-day/last-day date range, matching createAmountRequest.
   */
  const createTimeBalancesRequest = (
    form: SalaryDeviationFormTypes.WintDeviationCreate
  ): WintSalaryDeviationsApi.SalaryDeviationCreateRequest => {
    const fromDate = DateHelper.getFirstDayOfMonth(form.timeBalances?.yearMonth ?? new Date());
    const toDate = DateHelper.getLastDayOfMonth(form.timeBalances?.yearMonth ?? new Date());

    // No comment: the backend rejects comments for non-Compensation deviations
    // (CommentNotAllowedForNonCompensationDeviation), and the form gates the
    // comment field to the Compensation category, so Komp never carries one.
    return {
      personId: Number(form.employee?.id),
      deviationTypeId: form.deviation?.id != null ? String(form.deviation.id) : "",
      fromDate: fromDate,
      toDate: toDate,
      value: form.timeBalances?.value,
      dayMinutes: [],
      amountDescriptions: [],
      useCurrentMonthPlacement: form.useCurrentMonthPlacement ?? false,
    };
  };

  /**
   * Creates API request for saved-salary / vacation-pay withdrawals and the saved-salary deposit.
   *
   * The amount goes in the scalar `value`, NOT amountDescriptions. The seeded withdrawal events
   * are OutputType.Number with an "[Output]" pass-through, so InputResolver feeds them the
   * period's Value and that becomes the gross; nothing converts an amount-description list into
   * that input. An amount sent as amountDescriptions passes validation, saves cleanly, and pays
   * nothing. Wint.Salary's deposit rules read the same `value` and refuse a deposit without one.
   * These are the only Compensations + DecimalValue types that do not use the list.
   */
  const createWithdrawalRequest = (
    form: SalaryDeviationFormTypes.WintDeviationCreate
  ): WintSalaryDeviationsApi.SalaryDeviationCreateRequest => {
    const fromDate = DateHelper.getFirstDayOfMonth(form.withdrawal?.yearMonth ?? new Date());
    const toDate = DateHelper.getLastDayOfMonth(form.withdrawal?.yearMonth ?? new Date());

    return {
      personId: Number(form.employee?.id),
      deviationTypeId: form.deviation?.id != null ? String(form.deviation.id) : "",
      fromDate: fromDate,
      toDate: toDate,
      comment: form.comment,
      value: form.withdrawal?.amount,
      dayMinutes: [],
      amountDescriptions: [],
      useCurrentMonthPlacement: form.useCurrentMonthPlacement ?? false,
    };
  };

  /**
   * Fuel benefit: only inputs go up. Salary computes value itself and refuses a value or amount
   * list beside fuel inputs, so neither is sent. Only builder that writes fuelBenefit — keeps
   * stale fuel values off other types.
   */
  const createFuelBenefitRequest = (
    form: SalaryDeviationFormTypes.WintDeviationCreate
  ): WintSalaryDeviationsApi.SalaryDeviationCreateRequest => {
    const fromDate = DateHelper.getFirstDayOfMonth(form.fuelBenefit?.yearMonth ?? new Date());
    const toDate = DateHelper.getLastDayOfMonth(form.fuelBenefit?.yearMonth ?? new Date());

    return {
      personId: Number(form.employee?.id),
      deviationTypeId: form.deviation?.id != null ? String(form.deviation.id) : "",
      fromDate: fromDate,
      toDate: toDate,
      comment: form.comment,
      dayMinutes: [],
      amountDescriptions: [],
      fuelBenefit: {
        fuelCost: Number(form.fuelBenefit?.fuelCost),
        privateDistance: Number(form.fuelBenefit?.privateDistance),
        businessDistance: SalaryDeviationHelper.toOptionalNumber(form.fuelBenefit?.businessDistance),
      },
      useCurrentMonthPlacement: form.useCurrentMonthPlacement ?? false,
    };
  };

  /**
   * Creates API request for travel compensation deviations
   * Used by Compensation type with TravelCompensationDomestic/International
   */
  const createTravelCompensationRequest = (
    form: SalaryDeviationFormTypes.WintDeviationCreate
  ): WintSalaryDeviationsApi.SalaryDeviationCreateRequest => {
    const fromDate = form.travelCompensation!.fromDate!;
    const toDate = form.travelCompensation!.toDate!;

    const formatTime = (time?: string): string => {
      if (!time) return "00:00:00";
      const parts = time.split(":");
      if (parts.length < 2) return "00:00:00";
      const hours = parts[0].padStart(2, "0");
      const minutes = parts[1].padStart(2, "0");
      return `${hours}:${minutes}:00`;
    };

    const travelCompensation: WintSalaryDeviationsApi.TravelCompensation = {
      countryCode: form.travelCompensation?.countryCode?.id as string,
      startTime: formatTime(form.travelCompensation?.fromTime) as any,
      endTime: formatTime(form.travelCompensation?.toTime) as any,
      days:
        form.travelCompensation?.mealDeductions
          ?.filter((meal) => meal.day != null)
          .map(
            (meal): WintSalaryDeviationsApi.TravelCompensationDay => ({
              date: meal.day!,
              breakfastPaid: meal.breakfast ?? false,
              lunchPaid: meal.lunch ?? false,
              dinnerPaid: meal.dinner ?? false,
              nightlyAllowancePaid: false,
            })
          ) ?? [],
    };

    return {
      personId: Number(form.employee?.id),
      deviationTypeId: form.deviation?.id != null ? String(form.deviation.id) : "",
      fromDate: fromDate,
      toDate: toDate,
      comment: form.comment,
      dayMinutes: [],
      amountDescriptions: [],
      travelCompensation: travelCompensation,
      useCurrentMonthPlacement: form.useCurrentMonthPlacement ?? false,
    };
  };

  const calculateMinutes = (hours: number, minutes: number): number => hours * 60 + minutes;

  const getEffectiveDateRange = (
    fromDate: Date | null | undefined,
    toDate: Date | null | undefined
  ): { from: Date; to: Date } => {
    if (!fromDate) {
      throw new Error("From date is required");
    }
    return {
      from: fromDate,
      to: toDate || fromDate,
    };
  };

  const createDateRange = (startDate: Date, endDate: Date, minutes: number): WintSalaryDeviationsApi.DayMinutes[] => {
    const result: WintSalaryDeviationsApi.DayMinutes[] = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      result.push({
        day: new Date(current),
        minutes,
      });
      current.setDate(current.getDate() + 1);
    }

    return result;
  };

  const generateDateHours = (
    timeDeviation: SalaryDeviationFormTypes.TimeDeviationCreate,
    deviationType?: WintSalaryEnums.SalaryDeviationType
  ): WintSalaryDeviationsApi.DayMinutes[] | undefined => {
    const { reportType, dateRange, time, fillDayHours, dayHours } = timeDeviation;
    const fromDate = dateRange?.start;
    const toDate = dateRange?.end;
    const isAbsence = SalaryDeviationHelper.isAbsenceDeviation(deviationType);

    switch (reportType) {
      case WintSalaryEnums.SalaryDeviationReportType.FullDay: {
        return undefined;
      }

      case WintSalaryEnums.SalaryDeviationReportType.Time: {
        if (fillDayHours && dayHours) {
          return dayHours
            ?.filter((dh) => dh.day != null)
            .map(
              (dh): WintSalaryDeviationsApi.DayMinutes => ({
                day: dh.day!,
                minutes: calculateMinutes(+(dh.hours ?? 0), +(dh.minutes ?? 0)),
              })
            );
        }

        if (!fillDayHours && dayHours?.length) {
          const userMinutes = calculateMinutes(+(time?.hours ?? 0), +(time?.minutes ?? 0));
          return dayHours
            .filter((dh) => dh.day != null)
            .map(
              (dh): WintSalaryDeviationsApi.DayMinutes => ({
                day: dh.day!,
                // Only cap minutes with maxTotalMinutes for Absence category
                minutes: isAbsence ? Math.min(userMinutes, dh.maxTotalMinutes) : userMinutes,
              })
            );
        }

        const { from, to } = getEffectiveDateRange(fromDate, toDate);
        const timeInMinutes = calculateMinutes(+(time?.hours ?? 0), +(time?.minutes ?? 0));
        return createDateRange(from, to, timeInMinutes);
      }

      default:
        throw new Error(`Unsupported report type: ${reportType}`);
    }
  };

  const createDeviation = (form: SalaryDeviationFormTypes.WintDeviationCreate) => {
    const deviationPost = convertCreateFormToApiPost(form);
    return createDeviationApi(deviationPost);
  };

  const createDeviationReapprove = (form: SalaryDeviationFormTypes.WintDeviationCreate) => {
    const deviationPost = convertCreateFormToApiPost(form);
    return createDeviationReApproveApi(deviationPost);
  };

  const updateDeviation = (id: string, form: SalaryDeviationFormTypes.WintDeviationCreate, yearMonthFrom?: number) => {
    const deviationPut = convertUpdateFormToApiPut(form, yearMonthFrom);
    return updateDeviationApi(id, deviationPut);
  };

  const updateDeviationReapprove = (
    id: string,
    form: SalaryDeviationFormTypes.WintDeviationCreate,
    yearMonthFrom?: number,
    yearMonthTo?: number
  ) => {
    const deviationPut = convertUpdateFormToApiPut(form, yearMonthFrom, yearMonthTo);
    return updateDeviationReApproveApi(id, deviationPut);
  };

  const getDeviationById = async (id: string, yearMonthFrom?: number, yearMonthTo?: number) => {
    const response = await getDeviationByIdApi(id, yearMonthFrom, yearMonthTo);
    return response.data;
  };

  const getPartialDeviationById = async (id: string, startYearMonth?: number, endYearMonth?: number) => {
    const response = await getDeviationByIdApi(id, startYearMonth, endYearMonth);
    return response.data;
  };

  const getAllDeviationTypes = async (personExternalId?: number): Promise<AutocompleteListItem[]> => {
    const deviationTypes = await getDeviationTypesApi(personExternalId !== undefined ? { personExternalId } : {});
    return deviationTypes.map((type) => deviationTypeToAutocompleteListItem(type));
  };

  const searchDeviationTypes = async (search: string): Promise<AutocompleteListItem[]> => {
    const filter: WintSalaryDeviationsApi.SalaryDeviationTypeFilter = {
      name: search,
    };
    const deviationTypes = await getDeviationTypesApi(filter);
    return deviationTypes.map((type) => deviationTypeToAutocompleteListItem(type));
  };

  const deviationTypeToAutocompleteListItem = (
    type: WintSalaryDeviationsApi.SalaryDeviationType
  ): AutocompleteListItem => {
    return {
      id: type.id,
      value: type.name ?? "",
      data: type,
      // Commission is created only by the salary engine, so it is never offered even if the type is
      // not flagged hidden. An existing commission deviation still resolves its type for editing.
      hide: (options: AutocompleteListItem[], value?: string, isValueFromOptions?: boolean) =>
        (type.hidden ?? false) ||
        type.informationType === WintSalaryDeviationsApi.SalaryDeviationInformationType.Commission,
    };
  };

  // A system-managed type linked to no company is missing from the company's type list, so its rows would open as
  // an empty absence. Each is seeded as a signed Compensations + DecimalValue amount, so the row's own name and
  // value are all the read-only form needs. Any other missing type gets no guessed shape: its row is editable.
  const systemManagedTypeFromDeviation = (
    salaryDeviation: WintSalaryDeviationsApi.SalaryDeviation
  ): AutocompleteListItem | undefined => {
    const id = salaryDeviation.deviationTypeId;
    if (!id || !excludedDeviationTypeIds.includes(id)) return undefined;

    const name = salaryDeviation.deviationTypeName ?? "";
    const type: WintSalaryDeviationsApi.SalaryDeviationType = {
      id,
      name,
      category: WintSalaryDeviationsApi.SalaryDeviationTypeCategory.Compensations,
      informationType: WintSalaryDeviationsApi.SalaryDeviationInformationType.DecimalValue,
      hidden: true,
      classification: WintSalaryDeviationsApi.SalaryDeviationTypeClassification.None,
      currentMonthPlacement: WintSalaryDeviationsApi.CurrentMonthPlacement.Never,
    };
    return { id, value: name, data: type };
  };

  const convertApiResponseToForm = (
    salaryDeviation: WintSalaryDeviationsApi.SalaryDeviation,
    deviationTypes: AutocompleteListItem[],
    template?: WintSalaryDeviationsApi.DayMinutesTemplate[],
    canManageSalary?: boolean
  ): SalaryDeviationFormTypes.WintDeviationCreate => {
    const deviation =
      deviationTypes.find((type) => type.id === salaryDeviation.deviationTypeId) ??
      systemManagedTypeFromDeviation(salaryDeviation);
    const deviationTypeData: WintSalaryDeviationsApi.SalaryDeviationType | undefined = deviation?.data;

    const ensureDate = (value: Date | string): Date => {
      return value instanceof Date ? value : new Date(value);
    };

    const templateByDate = new Map<string, number>();
    if (template) {
      template.forEach((t) => {
        templateByDate.set(getDateKey(t.day), t.totalMaxMinutes);
      });
    }

    const lockedYearMonths = new Set<number>();
    if (salaryDeviation.periodYearMonthStates) {
      salaryDeviation.periodYearMonthStates.forEach((pState) => {
        const isLocked = canManageSalary
          ? pState.isYearMonthFinalized
          : pState.state === WintSalaryDeviationsApi.SalaryDeviationState.Approved ||
            pState.state === WintSalaryDeviationsApi.SalaryDeviationState.SoftApproved ||
            pState.isYearMonthFinalized;
        if (isLocked) {
          lockedYearMonths.add(pState.yearMonth);
        }
      });
    }

    let formDeviationType: WintSalaryEnums.SalaryDeviationType;
    let timeDeviation: SalaryDeviationFormTypes.TimeDeviationCreate | undefined;
    let amount: SalaryDeviationFormTypes.AmountCreate | undefined;
    let travelCompensation: SalaryDeviationFormTypes.TravelCompensationCreate | undefined;
    let timeBalances: SalaryDeviationFormTypes.TimeBalancesCreate | undefined;
    let withdrawal: SalaryDeviationFormTypes.WithdrawalCreate | undefined;

    switch (deviationTypeData?.category) {
      case WintSalaryDeviationsApi.SalaryDeviationTypeCategory.Absence:
        formDeviationType = WintSalaryEnums.SalaryDeviationType.Absence;
        break;
      case WintSalaryDeviationsApi.SalaryDeviationTypeCategory.WorkedHours:
        formDeviationType = WintSalaryEnums.SalaryDeviationType.WorkingHours;
        break;
      case WintSalaryDeviationsApi.SalaryDeviationTypeCategory.Compensations:
        formDeviationType = WintSalaryEnums.SalaryDeviationType.Compensation;
        break;
      case WintSalaryDeviationsApi.SalaryDeviationTypeCategory.TimeBalances:
        formDeviationType = WintSalaryEnums.SalaryDeviationType.TimeBalances;
        break;
      default:
        formDeviationType = WintSalaryEnums.SalaryDeviationType.Absence;
    }

    const isDecimalValue =
      deviationTypeData?.informationType === WintSalaryDeviationsApi.SalaryDeviationInformationType.DecimalValue;
    const isTravelCompensation =
      (deviationTypeData?.informationType ===
        WintSalaryDeviationsApi.SalaryDeviationInformationType.TravelCompensationDomestic ||
        deviationTypeData?.informationType ===
          WintSalaryDeviationsApi.SalaryDeviationInformationType.TravelCompensationInternational) &&
      deviationTypeData?.category === WintSalaryDeviationsApi.SalaryDeviationTypeCategory.Compensations;

    const isVacation =
      deviationTypeData?.classification === WintSalaryDeviationsApi.SalaryDeviationTypeClassification.Vacation;

    if (
      formDeviationType === WintSalaryEnums.SalaryDeviationType.Absence ||
      (formDeviationType === WintSalaryEnums.SalaryDeviationType.WorkingHours && !isDecimalValue)
    ) {
      const dayHourForm: SalaryDeviationFormTypes.HoursByDayWithMeta[] = isVacation
        ? []
        : salaryDeviation.dayMinutes?.map((dM: WintSalaryDeviationsApi.DayMinutes, index: number) => {
            const dayDate = dM.day ? ensureDate(dM.day) : undefined;
            const dateKey = dM.day ? getDateKey(dM.day) : "";
            const isDayLocked = dayDate ? lockedYearMonths.has(DateHelper.getYearMonth(dayDate)) : false;
            const maxMinutes = isDayLocked ? 0 : (templateByDate.get(dateKey) ?? 1440);
            return {
              id: index + 1,
              day: dayDate,
              hours: Math.floor(dM.minutes / 60),
              minutes: dM.minutes % 60,
              maxTotalMinutes: maxMinutes,
            };
          }) || [];

      // Vacation deviations are always full days - load as FullDay so the hours/minutes UI
      // stays hidden. Saving without dayMinutes lets the backend regenerate full schedule days.
      timeDeviation = {
        dateRange: {
          start: ensureDate(salaryDeviation.fromDate),
          end: ensureDate(salaryDeviation.toDate),
        },
        selectedPeriod: ensureDate(salaryDeviation.fromDate),
        reportType: isVacation
          ? WintSalaryEnums.SalaryDeviationReportType.FullDay
          : WintSalaryEnums.SalaryDeviationReportType.Time,
        fillDayHours: !isVacation,
        dayHours: dayHourForm,
        time: undefined,
        yearMonthStates: salaryDeviation.periodYearMonthStates,
      };
    }

    const isWithdrawalOrDeposit = SalaryDeviationHelper.takesSingleAmount(deviationTypeData);

    if (isWithdrawalOrDeposit) {
      withdrawal = {
        yearMonth: ensureDate(salaryDeviation.fromDate),
        amount: salaryDeviation.value,
      };
    }

    const isCommission =
      deviationTypeData?.informationType === WintSalaryDeviationsApi.SalaryDeviationInformationType.Commission;

    if (
      ((formDeviationType === WintSalaryEnums.SalaryDeviationType.WorkingHours && isDecimalValue) ||
        (formDeviationType === WintSalaryEnums.SalaryDeviationType.Compensation && (isDecimalValue || isCommission))) &&
      !isWithdrawalOrDeposit
    ) {
      const isHoursUnit =
        deviationTypeData?.informationTypeUnit === WintSalaryDeviationsApi.SalaryDeviationInformationTypeUnit.Hours;

      const mapAmountToSumDescription = (amount: number, description: string | undefined, index: number) => {
        const base = { id: index + 1, sum: amount, description };
        if (isHoursUnit) {
          const totalMinutes = Math.round(amount * 60);
          return {
            ...base,
            hours: Math.floor(totalMinutes / 60),
            minutes: totalMinutes % 60,
          };
        }
        return base;
      };

      const sumDescriptions = salaryDeviation.amountDescriptions?.length
        ? salaryDeviation.amountDescriptions.map((value, index) =>
            mapAmountToSumDescription(value.amount, value.description, index)
          )
        : salaryDeviation.value !== undefined
          ? [mapAmountToSumDescription(salaryDeviation.value, "", 0)]
          : [];

      amount = {
        yearMonth: ensureDate(salaryDeviation.fromDate),
        sumDescriptions,
      };
    }

    if (isTravelCompensation) {
      const travelComp = salaryDeviation.travelCompensation;
      const fromDate = ensureDate(salaryDeviation.fromDate);
      const toDateValue = ensureDate(salaryDeviation.toDate);

      const parseTime = (time?: any): string | undefined => {
        return time ? String(time).substring(0, 5) : undefined;
      };

      const countryCodeItem = travelComp?.countryCode
        ? CountryHelper.getCountryAutoCompleteList().find((country) => country.id === travelComp.countryCode)
        : undefined;

      travelCompensation = {
        fromDate: fromDate,
        toDate: toDateValue,
        fromTime: parseTime(travelComp?.startTime),
        toTime: parseTime(travelComp?.endTime),
        countryCode: countryCodeItem,
        mealDeductions:
          travelComp?.days?.map((day, index) => ({
            id: index + 1,
            day: ensureDate(day.date),
            breakfast: day.breakfastPaid ?? false,
            lunch: day.lunchPaid ?? false,
            dinner: day.dinnerPaid ?? false,
          })) ?? [],
      };
    }

    if (formDeviationType === WintSalaryEnums.SalaryDeviationType.TimeBalances) {
      timeBalances = {
        yearMonth: ensureDate(salaryDeviation.fromDate),
        value: salaryDeviation.value,
      };
    }

    const fuelBenefit: SalaryDeviationFormTypes.FuelBenefitCreate | undefined = SalaryDeviationHelper.isFuelBenefit(
      deviation
    )
      ? {
          yearMonth: ensureDate(salaryDeviation.fromDate),
          fuelCost: salaryDeviation.fuelBenefit?.fuelCost,
          privateDistance: salaryDeviation.fuelBenefit?.privateDistance,
          businessDistance: salaryDeviation.fuelBenefit?.businessDistance,
        }
      : undefined;

    return {
      employee: {
        id: salaryDeviation.reportedPersonId?.toString(),
        value: salaryDeviation.reportedPersonName || "",
      },
      deviationType: formDeviationType,
      deviation: deviation,
      comment: salaryDeviation.comment,
      timeDeviation: timeDeviation,
      amount: amount,
      travelCompensation: travelCompensation,
      timeBalances: timeBalances,
      withdrawal: withdrawal,
      fuelBenefit: fuelBenefit,
      isFullDeviationLoaded: salaryDeviation.fullDeviationLoaded,
      useCurrentMonthPlacement: salaryDeviation.useCurrentMonthPlacement,
      child: salaryDeviation.childId
        ? {
            id: salaryDeviation.childId,
            value: salaryDeviation.childName ?? "",
          }
        : undefined,
    };
  };

  const deleteDeviation = async (id: string) => {
    return deleteDeviationApi(id, undefined, undefined);
  };

  const deletePartialDeviation = async (id: string, startYearMonth?: Date, endYearMonth?: Date) => {
    const startYm = startYearMonth ? DateHelper.getYearMonth(startYearMonth) : undefined;
    const endYm = endYearMonth ? DateHelper.getYearMonth(endYearMonth) : undefined;
    return deleteDeviationApi(id, startYm, endYm);
  };

  const deleteDeviationReapprove = async (id: string) => {
    return deleteDeviationReApproveApi(id, undefined, undefined);
  };

  const deletePartialDeviationReapprove = async (id: string, startYearMonth?: Date, endYearMonth?: Date) => {
    const startYm = startYearMonth ? DateHelper.getYearMonth(startYearMonth) : undefined;
    const endYm = endYearMonth ? DateHelper.getYearMonth(endYearMonth) : undefined;
    return deleteDeviationReApproveApi(id, startYm, endYm);
  };

  const getDeviationReport = async (
    filter: SalaryDeviationFormTypes.WintSalaryDeviationReportFilter
  ): Promise<WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRow[] | undefined> => {
    const apiFilter = reportToApiFilter(filter);
    const response = await getDeviationReportApi(apiFilter);
    return response.data.personSummaries;
  };

  const reportToApiFilter = (
    filter: SalaryDeviationFormTypes.WintSalaryDeviationReportFilter
  ): WintSalaryDeviationsApi.DeviationReportFilter => {
    return {
      startPeriod: DateHelper.getYearMonth(filter.yearMonthRange.start ?? new Date()),
      endPeriod: DateHelper.getYearMonth(filter.yearMonthRange.end ?? new Date()),
      personExternalIds: filter.persons?.filter((person) => person.id !== undefined).map((person) => Number(person.id)),
      salaryDeviationTypeIds: filter.deviationTypes?.map((type) => type.id as string),
      deviationCategories: filter.categoryTypes?.map(
        (type) => type.id as WintSalaryDeviationsApi.SalaryDeviationTypeCategory
      ),
      employeeName: filter.employeeSearchName,
    };
  };

  const getDeviationCategoryText = (category: WintSalaryDeviationsApi.SalaryDeviationTypeCategory) => {
    return LocalizationHelperInstance.getTranslatedEnumValue("SalaryDeviation.DeviationCategory", category);
  };

  const getValidDeviationCategoriesSelectionItems = (): SelectionItem[] => {
    return EnumHelper.getNumericItemValues(WintSalaryDeviationsApi.SalaryDeviationTypeCategory).map((category) => ({
      id: category,
      value: getDeviationCategoryText(category),
    }));
  };

  /**
   * Filters dayHours to only include entries from the specified month.
   */
  const filterDayHoursByMonth = (
    dayHours: SalaryDeviationFormTypes.HoursByDayWithMeta[],
    selectedMonth: Date
  ): SalaryDeviationFormTypes.HoursByDayWithMeta[] => {
    const targetYear = selectedMonth.getFullYear();
    const targetMonth = selectedMonth.getMonth();
    return dayHours.filter((dh) => {
      if (!dh.day) return false;
      const d = dh.day instanceof Date ? dh.day : new Date(dh.day);
      return d.getFullYear() === targetYear && d.getMonth() === targetMonth;
    });
  };

  /**
   * The single source of truth for "can this deviation period be edited".
   * Finalized periods are always locked. Approved/SoftApproved periods are
   * locked unless the user has salary-manager permissions.
   *
   * Both the per-month check inside the deviation dialog and the per-row check
   * in the deviation report go through this — keeping a second copy of the rule
   * lets the report open a row in edit mode that the dialog then treats as
   * locked, producing an enabled save button that silently does nothing.
   */
  const isDeviationStateLocked = (
    state: WintSalaryDeviationsApi.SalaryDeviationState | undefined,
    isFinalized: boolean | undefined,
    canManageSalary?: boolean
  ): boolean => {
    if (isFinalized) return true;
    if (canManageSalary) return false;
    return (
      state === WintSalaryDeviationsApi.SalaryDeviationState.Approved ||
      state === WintSalaryDeviationsApi.SalaryDeviationState.SoftApproved
    );
  };

  /**
   * Checks if a given date's month is locked for editing.
   */
  const isYearMonthLocked = (
    yearMonthStates: WintSalaryDeviationsApi.DeviationPeriodYearMonthState[] | undefined,
    date: Date | undefined,
    canManageSalary?: boolean
  ): boolean => {
    if (!yearMonthStates?.length || !date) return false;
    const ym = DateHelper.getYearMonth(date);
    const state = yearMonthStates.find((s) => s.yearMonth === ym);
    if (!state) return false;
    return isDeviationStateLocked(state.state, state.isYearMonthFinalized, canManageSalary);
  };

  /**
   * Merges current month's edits back into the full dayHours array by date key.
   */
  const mergeDayHoursEdits = (
    fullArray: SalaryDeviationFormTypes.HoursByDayWithMeta[],
    currentMonthEdits: SalaryDeviationFormTypes.HoursByDayWithMeta[]
  ): SalaryDeviationFormTypes.HoursByDayWithMeta[] => {
    const editsByDate = new Map<string, SalaryDeviationFormTypes.HoursByDayWithMeta>();
    currentMonthEdits.forEach((dh) => {
      if (dh.day) editsByDate.set(getDateKey(dh.day), dh);
    });
    return fullArray.map((dh) => {
      if (dh.day) {
        const edited = editsByDate.get(getDateKey(dh.day));
        if (edited) return edited;
      }
      return dh;
    });
  };

  /**
   * Validates that selectedPeriod is within the date range.
   * If not, returns the nearest valid month (either startDate's month or endDate's month).
   */
  const validateSelectedPeriod = (selectedPeriod: Date, startDate: Date, endDate: Date): Date => {
    const selectedYear = selectedPeriod.getFullYear();
    const selectedMonth = selectedPeriod.getMonth();

    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth();

    const endYear = endDate.getFullYear();
    const endMonth = endDate.getMonth();

    // If selected period is before range start, return start month
    if (selectedYear < startYear || (selectedYear === startYear && selectedMonth < startMonth)) {
      return new Date(startYear, startMonth, 1);
    }

    // If selected period is after range end, return end month
    if (selectedYear > endYear || (selectedYear === endYear && selectedMonth > endMonth)) {
      return new Date(endYear, endMonth, 1);
    }

    // Selected period is valid
    return selectedPeriod;
  };

  /**
   * Merges user edits from oldDayHours into newDayHours by date key.
   * Preserves hours/minutes but updates maxTotalMinutes from new data.
   */
  const mergeEditsIntoDayHours = (
    newDayHours: SalaryDeviationFormTypes.HoursByDayWithMeta[],
    oldDayHours: SalaryDeviationFormTypes.HoursByDayWithMeta[]
  ): SalaryDeviationFormTypes.HoursByDayWithMeta[] => {
    const editsByDate = new Map<string, SalaryDeviationFormTypes.HoursByDayWithMeta>();

    oldDayHours.forEach((dh) => {
      if (dh.day) {
        editsByDate.set(getDateKey(dh.day), dh);
      }
    });

    return newDayHours.map((newDH) => {
      if (newDH.day) {
        const existing = editsByDate.get(getDateKey(newDH.day));
        if (existing) {
          // Preserve hours/minutes from existing, but update maxTotalMinutes from new data
          return {
            ...existing,
            maxTotalMinutes: newDH.maxTotalMinutes,
          };
        }
      }
      return newDH;
    });
  };

  return {
    createDeviation,
    updateDeviation,
    getDeviationById,
    getPartialDeviationById,
    getAllDeviationTypes,
    convertApiResponseToForm,
    getEffectiveDateRange,
    deleteDeviation,
    deletePartialDeviation,
    getDeviationReport,
    getValidDeviationCategoriesSelectionItems,
    reportToApiFilter,
    searchDeviationTypes,
    excludedDeviationTypeIds,
    getDailyHoursTemplate,
    convertDailyHoursTemplateToForm,
    generateDefaultDayHours,
    filterDayHoursByMonth,
    mergeDayHoursEdits,
    validateSelectedPeriod,
    mergeEditsIntoDayHours,
    isDeviationStateLocked,
    isYearMonthLocked,
    updateDeviationReapprove,
    createDeviationReapprove,
    deleteDeviationReapprove,
    deletePartialDeviationReapprove,
    getChildrenForPersonApi,
    createChildForPersonApi,
    getWithdrawalBalancesApi,
  };
}

export default useWintSalaryDeviationService;
